import React, { useEffect, useState, useCallback } from "react";
import { Checkbox, Table } from "antd";
import "./RoamingItemPopUp.css";
import useDebounce from "hooks/useDebounce";
import RoamingItemPopUpFilterColumn from "./RoamingItemPopUpFilterColumn";
import { getRoamingItemsPopup } from "api/roamingItems.api";
import { IFilterValue, Ipagination, IRoamingItemsPopupView } from "domain/RoamingItems";

const RoamingItemPopUp: React.FC = () => {
  const [pagination, setPagination] = useState<Ipagination>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedRows, setSelectedRows] = useState<IRoamingItemsPopupView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<IRoamingItemsPopupView[]>([]);
  const [searchValues, setSearchValues] = useState<IFilterValue>({});
  const debouncedSearchValue = useDebounce(searchValues, 900);

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    dataIndex: string
  ) => {
    const value = e.target.value;
    setSearchValues((prev) => ({
      ...prev,
      [dataIndex]: value,
    }));
  };

  const buildFilterString = useCallback((): string => {
    if (!debouncedSearchValue || Object.keys(debouncedSearchValue).length === 0) {
      return "";
    }

    return Object.entries(debouncedSearchValue)
      .map(([key, value]) => `contains(${key}, '${String(value).replace(/'/g, "''")}')`)
      .join(" and ");
  }, [debouncedSearchValue]);

  const roamingItemsPopupColumns = [
    {
      title: "Roaming",
      dataIndex: "isRoaming",
      key: "isRoaming",
      render: (_: any, record: any) => (
        <Checkbox
          className={record.is_roaming ? "checkbox-disabled" : ""}
          checked={record.is_roaming || selectedRows.some(r => r.garment_id === record.garment_id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows((prev) => [...prev, record]);
            } else {
              setSelectedRows((prev) =>
                prev.filter((r) => r.garment_id !== record.garment_id)
              );
            }
          }}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    { title: "SKU", dataIndex: "sku", key: "sku" },
    { title: "Color", dataIndex: "color", key: "color" },
  ];

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const loadGarmentItemsView = useCallback(() => {
    setIsLoading(true);

    const { current, pageSize } = pagination;
    const skip = (current - 1) * pageSize;
    const filterStr = buildFilterString();

    getRoamingItemsPopup({ top: pageSize, skip, filter: filterStr })
      .then((data) => {
        const { value, "@odata.count": totalCount } = data;
        setPagination((prev) => ({
          ...prev,
          total: totalCount || 0,
        }));
        setTableData(value || []);
      })
      .finally(() => setIsLoading(false));
  }, [pagination.current, pagination.pageSize, buildFilterString]);

  useEffect(() => {
    loadGarmentItemsView();
  }, [pagination.current, pagination.pageSize, debouncedSearchValue, loadGarmentItemsView]);

  return (
    <Table
      scroll={{ x: "max-content" }}
      rowKey={"garment_id"}
      loading={isLoading}
      className="redesign-table"
      dataSource={tableData}
      columns={RoamingItemPopUpFilterColumn({
        columns: roamingItemsPopupColumns,
        searchValues,
        handleFilterChange,
        tableData,
        setSelectedRows
      })}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30"],
        onChange: handlePageChange,
      }}
    />
  );
};

export default RoamingItemPopUp;